/**
 * Solanaland
 *
 * Author: Phia Nasol
 * Company: Digiteer Inc.
 *
 * https://www.digiteer.digital/
 *
 */

import React from "react";
import { Row, Col, Container, Modal } from "react-bootstrap";
import { useAsyncDebounce } from "react-table";
import { Button, Card, Input, Select, SvgIcon, ControlledTable } from "core/components";
import formatter from "core/formatter";
import { navigateTo } from "core/navigation";
import moment from "moment";
import {
  admin_purchase_orders_path,
  admin_purchase_order_path,
  admin_purchase_requests_path,
  edit_admin_purchase_order_path,
  print_admin_purchase_order_path
} from "routes";
import { PurchaseOrderStatus } from "core/schema";
import { api } from "core/api";
import ControlledTableContext from "core/components/ControlledTableContext";

type PurchaseOrdersProps = {
  purchase_orders: Array<Record<string, unknown>>;
};

const PurchaseOrders = ({ has_purchase_orders, statuses }: PurchaseOrdersProps): JSX.Element => {
  const [purchaseOrders, setPurchaseOrders] = React.useState([]);
  const [purchaseOrder, setPurchaseOrder] = React.useState(null);
  const [showDeletePurchaseOrderModal, setShowDeletePurchaseOrderModal] = React.useState(false);
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [statusFilter, setStatusFilter] = React.useState("");
  const [isLoading, setIsLoading] = React.useState(false);
  const [pagination, setPagination] = React.useState(null);
  const [currentPage, setCurrentPage] = React.useState(1);
  const table = React.useRef(null);
  const abortController = React.useRef<AbortController>();
  const columns = React.useMemo(
    () => [
      {
        id: "id",
        Header: "Id",
        accessor: "id",
        Cell: () => {
          return <></>;
        }
      },
      {
        id: "code",
        Header: "PO No.",
        accessor: "code",
        Cell: (props) => {
          return (
            <div title={props.value} data-bs-toggle="tooltip">
              <p className="text-truncate text-center">{props.value}</p>
            </div>
          );
        }
      },
      {
        id: 2,
        Header: "Supplier ID",
        accessor: "supplier.code",
        Cell: (props) => {
          return (
            <div title={props.value} data-bs-toggle="tooltip">
              <p className="text-truncate text-center">{props.value}</p>
            </div>
          );
        }
      },
      {
        id: "supplier",
        Header: "Supplier",
        accessor: "supplier.name",
        Cell: (props) => {
          return (
            <div title={props.value} data-bs-toggle="tooltip">
              <p className="text-truncate">{props.value}</p>
            </div>
          );
        }
      },
      {
        id: 4,
        Header: "Warehouse",
        accessor: "warehouse.name",
        Cell: (props) => {
          return (
            <div title={props.value} data-bs-toggle="tooltip">
              <p className="text-center text-truncate">{props.value}</p>
            </div>
          );
        }
      },
      {
        Header: "Total Amount",
        accessor: "total_amount",
        Cell: (props) => {
          return (
            <div title={formatter.format(props.value)} data-bs-toggle="tooltip">
              <p className="text-truncate text-right">{formatter.format(props.value)}</p>
            </div>
          );
        }
      },
      {
        Header: "Created By",
        accessor: "created_by",
        Cell: (props) => {
          return (
            <div title={props.value} data-bs-toggle="tooltip">
              <p className="text-center text-truncate">{props.value}</p>
            </div>
          );
        }
      },
      {
        Header: "Date Created",
        accessor: "created_at",
        Cell: (props) => {
          return (
            <div title={moment(props.value).format("MM/DD/YYYY")} data-bs-toggle="tooltip">
              <p className="text-center text-truncate">{moment(props.value).format("MM/DD/YYYY")}</p>
            </div>
          );
        }
      },
      {
        id: "status",
        Header: "Status",
        accessor: "status",
        Cell: (props) => {
          const status = I18n.t(`activerecord.attributes.purchase_order.statuses.${props.value}`);

          return (
            <div
              className={`d-flex justify-content-center badge badge--pill badge--${props.value.replace("_", "-")}`}
              title={status}
              data-bs-toggle="tooltip"
            >
              <p className="text-truncate">{status}</p>
            </div>
          );
        }
      },
      {
        Header: "Actions",
        accessor: "",
        Cell: (props) => {
          const { status } = props.row.original;
          const isDraft = status == PurchaseOrderStatus.Draft;

          return (
            <div className="d-flex justify-content-center">
              <a
                className="button button--action button--action-success"
                title="View Purchase Order"
                data-bs-toggle="tooltip"
                onClick={() => {
                  window.open(edit_admin_purchase_order_path({ id: props.row.original.id }));
                }}
              >
                <SvgIcon name="view" className="h5" />
              </a>
              {![PurchaseOrderStatus.Draft, PurchaseOrderStatus.Cancelled].includes(status) && (
                <a
                  className="button button--action button--action-primary"
                  title="Print Purchase Order"
                  data-bs-toggle="tooltip"
                  onClick={() => {
                    window.open(print_admin_purchase_order_path(props.row.original.id));
                  }}
                >
                  <SvgIcon name="printer" className="h5" />
                </a>
              )}
              {isDraft && (
                <a
                  className="button button--action button--action-danger"
                  title="Delete Purchase Order"
                  data-bs-toggle="tooltip"
                  onClick={() => {
                    setPurchaseOrder(props.row.original);
                    setShowDeletePurchaseOrderModal(!showDeletePurchaseOrderModal);
                  }}
                >
                  <SvgIcon name="delete" className="h6" />
                </a>
              )}
            </div>
          );
        }
      }
    ],
    []
  );

  const deletePurchaseOrder = async (id) => {
    try {
      setIsSubmitting(true);
      await api.deleteItem(admin_purchase_order_path(id));
    } catch (error) {
      console.error(error);
    } finally {
      navigateTo(admin_purchase_orders_path());
    }
  };

  const getPurchaseOrders = React.useCallback(async ({ page, per, filters }) => {
    console.log("GETTING.");
    try {
      setIsLoading(true);
      // If there is a pending fetch request with associated AbortController, abort
      if (abortController.current) abortController.current.abort();
      // Assign a new AbortController for the latest fetch to our useRef variable
      abortController.current = new AbortController();
      const { signal } = abortController.current;

      const response = await api.get(`/admin/purchase_orders/page/${page}/per/${per}/filters/${JSON.stringify(filters)}`, { signal });
      setPagination(response.data.pagination);
      setPurchaseOrders(response.data.data);
      setCurrentPage(response.data.pagination?.current || currentPage);
      return { pagination: response.data.pagination, data: response.data.data };
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const onChange = useAsyncDebounce((column, value) => {
    table.current.setFilter(column, value);
  }, 400);

  React.useEffect(() => {
    const queryString = location.search;
    const urlParams = new URLSearchParams(queryString);
    const status = urlParams.get("status");

    if (status && ["draft", "open"].includes(status)) {
      setStatusFilter(status);
      table.current.setFilter("status", status);
    }
  }, []);

  return (
    <React.StrictMode>
      <ControlledTableContext.Provider value={{ currentPage, setCurrentPage }}>
        <Card>
          <Card.Header title="Purchase Orders">
            <Button
              className="button--brand"
              icon="plus"
              title="New Purchase Order"
              type="button"
              onClick={() => {
                navigateTo(admin_purchase_requests_path({ format: "html" }));
              }}
            />
          </Card.Header>
          <Card.Body>
            <Container fluid={true}>
              {has_purchase_orders ? (
                <>
                  <Row className="mb-4">
                    <Col>
                      <Input placeholder="Search for Purchase Order Number" onChange={(e) => onChange("code", e.target.value)} />
                    </Col>
                    <Col>
                      <Input placeholder="Search for Supplier Name" onChange={(e) => onChange("supplier", e.target.value)} />
                    </Col>
                    <Col>
                      <Select
                        placeholder="Filter by Status"
                        options={statuses}
                        isSearchable={false}
                        getOptionLabel={(option) => I18n.t(`activerecord.attributes.purchase_order.statuses.${option.value}`)}
                        getOptionValue={(option) => option.value}
                        onChange={(newOption) => {
                          if (newOption) {
                            setStatusFilter(newOption.value);
                            table.current.setFilter("status", newOption.value);
                          } else {
                            setStatusFilter("");
                            table.current.setFilter("status", "");
                          }
                        }}
                        value={statuses.find((status) => status.value == statusFilter) || ""}
                      />
                    </Col>
                  </Row>
                  <ControlledTable
                    columns={columns}
                    data={purchaseOrders}
                    ref={table}
                    isLoading={isLoading}
                    fetchData={getPurchaseOrders}
                    totalCount={pagination?.total_count}
                    totalPageCount={pagination?.total_pages || 0}
                    previous={pagination?.previous}
                    next={pagination?.next}
                  />
                </>
              ) : (
                <div className="text-center mt-4 mb-4">No available purchase orders.</div>
              )}
            </Container>
          </Card.Body>
        </Card>
      </ControlledTableContext.Provider>
      <Modal dialogClassName="modal-dialog-centered" show={showDeletePurchaseOrderModal}>
        <Modal.Header>{`Delete Purchase Order: ${purchaseOrder?.code}`}</Modal.Header>
        <Modal.Body>{`Are you sure you want to delete purchase order: ${purchaseOrder?.code}?`}</Modal.Body>
        <Modal.Footer>
          <Button
            onClick={() => {
              setShowDeletePurchaseOrderModal(!showDeletePurchaseOrderModal);
            }}
            icon="times"
            title="Cancel"
            loading={isSubmitting}
            disabled={isSubmitting}
          />
          <Button
            className="button--danger"
            loading={isSubmitting}
            disabled={isSubmitting}
            icon="delete"
            title="Yes, delete purchase order."
            onClick={() => {
              deletePurchaseOrder(purchaseOrder?.id);
            }}
          />
        </Modal.Footer>
      </Modal>
    </React.StrictMode>
  );
};

export default PurchaseOrders;
